import { __decorate } from "tslib";
import { Box2, Group, Vector2, Vector3 } from "three";
import { ToolType } from "../../Core/ITool.js";
import { Save, SaveChild } from "../../Core/Save.js";
import { ViewType } from "../../Models/AppModel.js";
import { bindedModelBase } from "../../Models/BindedModel.js";
import { Consts } from "../../Utils/Consts.js";
import { Default } from "../../Utils/Default.js";
import { Utils } from "../../Utils/Utils.js";
import { VectorUtils } from "../../Utils/VectorUtils.js";
import { AnatomicalAxis, AnatomicalAxisType } from "./AnatomicalAxis.js";
import { AnatomicalAxis2 } from "./AnatomicalAxis2.js";
import { ApexCalculator } from "./ApexCalculator.js";
import { ThirdLine } from "./ThirdLine.js";
export class FullAnatomicalAxis extends Group {
    onBeforeRestore(obj) {
        obj._isThirdLineInserted && this.switchApex();
    }
    onAfterRestore() {
        this.updateApex();
    }
    get bisector() {
        const ref = this.reference;
        const mov = this.moving;
        return VectorUtils.getBisector(ref.AB, mov.AB);
    }
    constructor(viewType, convexities, refType, side, boneType, planType, angle, translHor, translVer, apexCalculatorType = ApexCalculator, thirdLineType = ThirdLine, anatAxisType = AnatomicalAxis) {
        super();
        this.toolType = ToolType.anatomicalAxis;
        this._box = new Box2();
        this._boxTolerance = 10;
        this._isThirdLineInserted = false;
        this._viewType = viewType;
        this._side = side;
        this._refType = refType;
        this._planType = planType;
        this._anatAxisType = anatAxisType;
        this.add(this.reference = new this._anatAxisType({ viewType, axisType: AnatomicalAxisType.reference, boneType, type: planType, refType, side, convexityList: convexities.Reference }), this.moving = new this._anatAxisType({ viewType, axisType: AnatomicalAxisType.moving, boneType, type: planType, refType, side, convexityList: convexities.Moving, angle, translHor, translVer }), this.apexCalculator = this._singleApexCalculator = new apexCalculatorType(viewType, this.reference, this.moving, planType, side, refType), this._thirdLine = new thirdLineType(this._viewType, this.reference, this.moving, this._side, this._refType, this._planType));
        this.reference.oppositePointLimit = this.moving.oppositePoint;
        this.moving.oppositePointLimit = this.reference.oppositePoint;
        this.reference.oppositeBLimit = this.moving.B.position;
        this.moving.oppositeBLimit = this.reference.B.position;
        this._pointsAxis1 = Utils.getAllDraggablePoints(this.reference);
        this._pointsAxis2 = Utils.getAllDraggablePoints(this.moving);
        this.updateApex();
        this.bindEvents();
    }
    bindEvents() {
        Default.bindOnEnabledChange_NoRaycastAndHiddenLabel(this, [this._thirdLine]);
        this.addEventListener('historyChanged', () => {
            this.apexCalculator.update();
            // called from above this.dispatchEvent({ type: 'updated' });
        });
        this.bindEvent("onDragMove", (args) => {
            if ((bindedModelBase.syncAnatomicalAxis && args.mesh.isDraggablePoint) || this.apexCalculator.apexes.map(x => x.hexagon).indexOf(args.mesh) > -1) { //todo add check name
                args.preventDefault = true;
                if (args.mesh == this.reference.A || args.mesh == this.moving.A) {
                    this.syncRotation(args);
                }
                else {
                    this.syncTranslation(args);
                }
            }
        });
        this.bindEvent('onAfterDragMove', (args) => {
            if (!bindedModelBase.syncAnatomicalAxis /* && (args.mesh as DraggablePoint).isDraggablePoint */) {
                this.updateApex();
            }
        });
    }
    switchProximal() {
        this.reference.dispose();
        if (this.reference instanceof AnatomicalAxis) {
            this.add(this.reference = new AnatomicalAxis2(this._viewType, AnatomicalAxisType.reference, new Vector3(0, 50, 0)));
        }
        else {
            // @ts-expect-error TODO fix when release it
            this.add(this.reference = new this._anatAxisType(undefined)); //todo fix
        }
        this.reference.oppositePointLimit = this.moving.oppositePoint;
        this.reference.oppositeBLimit = this.moving.B.position;
        this.apexCalculator.updateAxis(this.reference, this.moving);
        this._pointsAxis1 = Utils.getAllDraggablePoints(this.reference);
    }
    switchDistal() {
        this.moving.dispose();
        if (this.moving instanceof AnatomicalAxis) {
            this.add(this.moving = new AnatomicalAxis2(this._viewType, AnatomicalAxisType.moving, new Vector3(0, -50, 0)));
        }
        else {
            // @ts-expect-error TODO fix when release it
            this.add(this.moving = new this._anatAxisType(undefined)); //todo fix
        }
        this.moving.oppositePointLimit = this.reference.oppositePoint;
        this.moving.oppositeBLimit = this.reference.B.position;
        this.apexCalculator.updateAxis(this.reference, this.moving);
        this._pointsAxis2 = Utils.getAllDraggablePoints(this.moving);
    }
    switchApex() {
        if (this.apexCalculator === this._singleApexCalculator) {
            this.apexCalculator = this._thirdLine;
            this._isThirdLineInserted = true;
        }
        else {
            this.apexCalculator = this._singleApexCalculator;
            this._isThirdLineInserted = false;
        }
        this.updateApex();
    }
    syncTranslation(args) {
        const transl = args.position.clone().sub(args.mesh.position);
        this.moving.translateAllPoints(transl);
        this.reference.translateAllPoints(transl);
        this.apexCalculator.translateAllPoints(transl);
    }
    syncRotation(args) {
        const anatAxis = args.mesh.parent; //fix
        const anatAxisOpposite = anatAxis == this.moving ? this.reference : this.moving;
        const rotationCenter = anatAxisOpposite.A.position.clone();
        const angleToApply = VectorUtils.linesAngle(anatAxis.A.position, args.position, anatAxisOpposite.A.position);
        const angle = VectorUtils.linesAngle(anatAxis.A.position, anatAxis.B.position.clone().add(anatAxis.AB_dirOrigin), anatAxis.B.position);
        const angleOpposite = VectorUtils.linesAngle(anatAxisOpposite.A.position, anatAxisOpposite.B.position.clone().add(anatAxisOpposite.AB_dirOrigin), anatAxisOpposite.B.position);
        const excessAngle = Utils.getMaxExcessInSpecularRange(Math.PI / 2, angle - angleToApply, angleOpposite - angleToApply);
        const limitedAngleToApply = angleToApply + excessAngle;
        const newBc = anatAxis.middlePointB1B2.position.clone().sub(rotationCenter).applyAxisAngle(Consts.planeNormal, limitedAngleToApply).add(rotationCenter);
        const newBcOpp = anatAxisOpposite.middlePointB1B2.position.clone().sub(rotationCenter).applyAxisAngle(Consts.planeNormal, limitedAngleToApply).add(rotationCenter);
        if (VectorUtils.computeSign(newBc, newBcOpp, anatAxis.AB_dirOrigin) > 0) {
            this.moving.rotateAllPoints(limitedAngleToApply, rotationCenter);
            this.reference.rotateAllPoints(limitedAngleToApply, rotationCenter);
            this.apexCalculator.rotateAllPoints(limitedAngleToApply, rotationCenter);
        }
    }
    updateApex() {
        this.apexCalculator.update();
        const apexTooFarKey = this._viewType === ViewType.AP ? 'apexTooFarAP' : 'apexTooFarLT';
        bindedModelBase[apexTooFarKey] = !this.isInsideBox(this.apexCalculator.apexes[0]?.hexagon.position) || !this.isInsideBox(this.apexCalculator.apexes[1]?.hexagon.position);
    }
    /**
     * Check if at least a point is in the box.
     */
    isInsideBox(...positions) {
        if (positions[0] == undefined)
            return true;
        const pointsAxisX = [...this._pointsAxis1.map(point => point.position.x), ...this._pointsAxis2.map(point => point.position.x)];
        const pointsAxisY = [...this._pointsAxis1.map(point => point.position.y), ...this._pointsAxis2.map(point => point.position.y)];
        const box = this._box.set(new Vector2(Math.min(...pointsAxisX) - this._boxTolerance, Math.min(...pointsAxisY) - this._boxTolerance), new Vector2(Math.max(...pointsAxisX) + this._boxTolerance, Math.max(...pointsAxisY) + this._boxTolerance));
        for (const position of positions) {
            if (box.containsPoint(new Vector2(position.x, position.y))) {
                return true;
            }
        }
        return false;
    }
    reset() {
        this.reference.reset();
        this.moving.reset();
        this._thirdLine.reset();
        this.apexCalculator = this._singleApexCalculator;
        this.updateApex();
    }
    getAngleForCamera() {
        const ref = this.reference;
        return VectorUtils.linesAngleFromOrigin(ref.AB, ref.AB_dirOrigin);
    }
}
__decorate([
    SaveChild()
], FullAnatomicalAxis.prototype, "reference", void 0);
__decorate([
    SaveChild()
], FullAnatomicalAxis.prototype, "moving", void 0);
__decorate([
    SaveChild()
], FullAnatomicalAxis.prototype, "apexCalculator", void 0);
__decorate([
    Save()
], FullAnatomicalAxis.prototype, "_isThirdLineInserted", void 0);
//# sourceMappingURL=FullAnatomicalAxis.js.map